import React, { useState, useEffect, useContext } from "react";
import { useCallback, useMemo } from "react";
import { useQuery } from "../Hooks";
import { useTheme } from "styled-components";
import * as quoteReqs from "../../requests/quoteManagementRequests";
import * as constants from "../../modules/constants";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import UserContext from "../../context/user/UserContext";
import { Box, Flex } from "reflexbox";
import formatter from "../../modules/formatter";
import Table from "../UI/Table";
import Card from "../UI/Card";
import { Heading, Body } from "../UI/Text";
import { PageWrapper } from "../UI/Grid";
import Loading from "../UI/Loading";
import Form from "./Form";
import { start, finish } from "../../requests/apiRequests";
import ProductModel from "./ProductModel";
import TopBar from "./TopBar";
import { GenerateGUID } from "../../modules/guid";
import { CreateUserModal, CancelModal } from "./Modals";
import { getProduct } from "../../requests/productManagementRequests";
import _ from "lodash";
import AlertContext from "../../context/alert/AlertContext";
import { getCustomer } from "../../requests/customerManagementRequests";

const Configurator = () => {
  const navigate = useNavigate();
  const { createLine, createQuote, updateLine } = quoteReqs;

  const {
    productId: paramProductId,
    quoteId,
    configurationId: paramConfigId,
  } = useParams();

  const configuratorContext = useContext(ConfiguratorContext);
  const {
    activeUser,
    platform,
    queries,
    setQueries,
    pid,
    isLoading: userLoading,
  } = useContext(UserContext);
  const { showFailureAlert, showWarningAlert, showSuccessAlert } =
    useContext(AlertContext);
  const { requireHttps } = platform || {};

  const {
    isReconfigure: isReconfig,
    api,
    setApi,
    currentPage,
    setCurrentPage,
    setSessionId,
    setACMode,
    optionToFocus,
    setOptionToFocus,
    setActiveProduct,
    setQuote,
  } = configuratorContext;

  const { ConfiguredPrice, ImageUrl, Details = [] } = api?.d || {};

  const [searchParams] = useSearchParams();
  const sourceConfigurationId = searchParams.get("configurationId") || "";
  const sourceHeaderId = searchParams.get("headerId") || "";
  const lineNumber = searchParams.get("lineNumber") || "";
  const LineId = searchParams.get("LineId") || "";

  const productId = +paramProductId || 41;

  const isReconfigure = useMemo(() => {
    if (paramConfigId === sourceConfigurationId) return true;
    // else if (!!sourceConfigurationId && paramConfigId !== sourceConfigurationId)
    //   return false;
    else return isReconfig;
  }, [isReconfig, paramConfigId, sourceConfigurationId]);

  const PRODUCT = useQuery({
    queryID: productId,
    request: getProduct,
    queryVariables: { id: productId },
    forcedFilters: {},
    skip: userLoading,
  });

  const {
    data: product,
    isLoading: productLoading,
    refetchQuery,
  } = PRODUCT || {};

  const [showCancel, setShowCancel] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const { data: quote, isLoading: quoteLoading } = useQuery({
    request: quoteReqs.getQuoteById,
    queryVariables: { id: quoteId },
    forcedFilters: {},
    skip: userLoading,
  });

  const customerQueryVariables = useMemo(() => {
    if (quote?.contactId) return { id: quote?.contactId };
    else return { quoteId };
  }, [quote, quoteId]);

  const _customer = useQuery({
    queryID: `customer|quote|${quoteId}`,
    request: getCustomer,
    queryVariables: customerQueryVariables,
    forcedFilters: {},
    skip: userLoading,
  });

  useEffect(() => {
    if (!!product) setActiveProduct(product);
  }, [product, setActiveProduct]);

  useEffect(() => {
    if (!!quote && !!_customer.data)
      setQuote({ ...quote, customer: _customer.data?.customer });
  }, [_customer.data, quote, setQuote]);  

  const configurationId = useMemo(
    () =>
      !activeUser
        ? `${quoteId}_${lineNumber || quote?.lines?.length || 1}`
        : paramConfigId,
    [activeUser, lineNumber, paramConfigId, quote, quoteId]
  );

  useEffect(() => {
    setApi(null);
  }, [LineId, setApi, sourceConfigurationId, quoteId, configurationId]);

  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const startConfigurator = useCallback(
    async (signal) => {
      if (data?.ui) return;
      
      setLoading(true);      
      const { prepare, initialize, ui } = await start({       
        signal,
        productId: paramProductId,
        configurationId: paramConfigId,
        isReconfigure: isReconfigure,
        sourceHeaderId: sourceHeaderId || "",
        sourceConfigurationId: sourceConfigurationId || "",
        requireHttps: platform?.requireHttps,        
        showFailureAlert,
        quoteId,
        cpqProfileOption: _customer?.data?.customer?.cpqProfileOption,
      });       

      setData({ prepare, initialize, ui });
      setLoading(false);
      return;
    },
    [
      paramProductId,
      isReconfigure,
      paramConfigId,
      platform,
      showFailureAlert,
      sourceConfigurationId,
      sourceHeaderId,
      quoteId,
      data,
      _customer.data?.customer?.cpqProfileOption,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    if ((!data && isLoading) || userLoading) return;

    startConfigurator(controller.signal);

    return () => controller.abort();
  }, [
    _customer.data,
    _customer.isLoading,
    isLoading,
    platform,
    product,
    data,
    productLoading,
    quote,
    quoteLoading,
    startConfigurator,
    userLoading,
  ]);

  const { data: ui, config } = data?.ui || {};
  const { d: sessionID } = data?.initialize || {};
  const { headerId: _headerId } = JSON.parse(config?.data.headers || "{}");

  useEffect(() => {
    if (api?.d?.Pages?.length === 0) return;
    const lastValue = api?.d?.Pages?.length - 1;
    const maxScreenOptions = api?.d?.Pages[lastValue].Screens.slice()
      .reverse()
      .find((s) => s.ScreenOptions.length > 0);

    const { ScreenOptions } = maxScreenOptions || {};
    const { length } = ScreenOptions || [];
    const { [length - 1]: lastScreenOption } = ScreenOptions || {};
    const acMode = lastScreenOption?.AutoContinueMode;
    if (acMode === 2 && optionToFocus === lastScreenOption?.ID && !lastScreenOption?.Value) {
      setCurrentPage(lastValue);
    }

    setACMode(acMode);
  }, [api, optionToFocus, setACMode, setCurrentPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    if (_.isArray(api?.d?.Messages)) {
      api.d.Messages.forEach((message) => {
        const { Critical, Warning, Information } = constants.MESSAGE_RULE_TYPES;
        switch (message.Type) {
          case Critical:
            showFailureAlert(message.Value);
            break;
          case Warning:
            showWarningAlert(message.Value);
            break;
          case Information:
            showSuccessAlert(message.Value);
            break;
          default:
            showSuccessAlert(message.Value);
            break;
        }
        return;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    setSessionId(sessionID);
    setCurrentPage(0);
  }, [sessionID, setApi, setCurrentPage, setSessionId]);

  useEffect(() => {
    if (ui) {
      setApi(ui);
      setOptionToFocus(ui.d?.OptionToFocus);
    }
  }, [ui, setApi, setOptionToFocus]);

  const finishConfiguration = useCallback(
    async ({ sessionId }) => {
      const { username, roleLevel } = activeUser || {};

      const publicUser = !roleLevel || !quoteId;

      await finish({ sessionId, requireHttps, productId });

      let _quoteId = quoteId;

      const line = {
        configurationId,
        headerId: _headerId || product?.HeaderId,
        imgUrl: ImageUrl,
        price: ConfiguredPrice,
        quantity: 1,
        productId,
        quoteId,
        description: product?.Description,
        partNumber: product?.ProductNumber,
        username: username || quote?.contact?.email,
        quoteCreated: publicUser ? "0" : "1",
        Details: JSON.stringify(api?.d?.Details),
      };

      setQuote({
        ...quote,
        lines: [...quote?.lines, line],
      });

      const newId = GenerateGUID();

      if (publicUser) {
        const newQuote = await createQuote({
          input: {
            username: username || quote?.contact?.email || activeUser?.username,
            createdByFirstName: activeUser?.firstName,
            createdByLastName: activeUser?.lastName,
            line,
            type: publicUser ? 3 : 1,
            _id: newId,
            pid,
            createdByUserId: activeUser?.id,
            groupId: activeUser?.groupId,
          },
        });

        if (publicUser) refetchQuery("quoteRequests");
        const { id: result } = newQuote || {};
        _quoteId = result;
      } else if (LineId) {
        await updateLine({
          id: LineId,
          input: {
            sourceConfigurationId:
              configurationId === sourceConfigurationId
                ? undefined
                : configurationId,
            Details: JSON.stringify(api?.d?.Details),
            price: ConfiguredPrice,
            imgUrl: ImageUrl,
            sessionId: sessionID,
          },
        });
        setQuote({
          ...quote,
          lines: quote?.lines?.map((l) =>
            LineId === l.id
              ? {
                  ...l,
                  Details: JSON.stringify(api?.d?.Details),
                  price: ConfiguredPrice,
                  imgUrl: ImageUrl,
                  sessionId: sessionID,
                }
              : l
          ),
        });
      } else {
        const newUUID = GenerateGUID();
        const newLine = await createLine({
          input: {
            ...line,
            sessionId: sessionID,
            quoteId,
            Details: JSON.stringify(api?.d?.Details),
            _id: newUUID,
          },
        });
        setQuote({
          ...quote,
          lines: [
            ...quote?.lines,
            {
              ...newLine,
              quoteId,
              Details: JSON.stringify(api?.d?.Details),
              _id: newUUID,
            },
          ],
        });
      }
      setQueries({
        ...queries,
        quotes: null,
        [`lines|${_quoteId}`]: null,
      });
      refetchQuery(`lines|${_quoteId}`);
      navigate(`/Quote/${_quoteId}`);
    },
    [
      ConfiguredPrice,
      ImageUrl,
      LineId,
      _headerId,
      sessionID,
      product,
      api,
      activeUser,
      configurationId,
      createLine,
      queries,
      setQueries,
      createQuote,
      navigate,
      productId,
      setQuote,
      quote,
      quoteId,
      refetchQuery,
      requireHttps,
      updateLine,
      sourceConfigurationId,
      pid,
    ]
  );

  // Configuration Details Table
  const DetailsSummary = ({Details}) => {
    const theme = useTheme();

    const columns = useMemo(() => [
        {
          id: "Caption",
          data: (row) => (
            <Body flex={1}>
              {row.Caption}
            </Body>
          ),
          // header: "Part",
          header: "",
          sort: false,
          size: { md: 7, sm: 7, xs: 7 },
        },
        {
          id: "value",
          data: (row) => (
            <Body right color={theme.secondary} flex={1}>
              {row.value}
            </Body>
          ),
          // header: "Selection",
          header: "",
          sort: false,
          size: { md: 3, sm: 3, xs: 3 },
        }
      ], [theme.secondary]
    );
  
    const gridData = useMemo(() => {
      const items = [];
      Details.forEach((Detail) => {
        const { Value, Type, Caption: _Caption } = Detail || {};        
        if (
          (!Value && Value !== false) ||
          Value?.startsWith("<hr") ||
          _Caption?.startsWith("<hr")
        )
          return;          
        var imgLink = false;
        if (Value?.startsWith("<IMG") || Value?.startsWith("<img")) {
          imgLink = true;
        }
        
        const [caption, ...rest] = _Caption.split(":");
        const formattedCapt = formatter.stripHtml(caption);                 
        
        const Caption = (
          <Body>            
            {formattedCapt}           
            <span style={{ color: theme.secondary }}>
              {typeof rest === "string" && rest?.startsWith("$")
                ? `$${rest}`
                : ""}
            </span>
          </Body>
        );          
        let value;                  

        // mod that handles the image link that comes in from configurator
        if (imgLink) {
         value = <img src={`${formatter.extractUrl(Value)}`} alt="imageUrl" />         
        } else {
          value = Type >= 5 ? `$${Value}` : formatter.stripHtml(Value);
        }        
     
        items.push({ Caption, value });
      });
      return items;
    }, [Details, theme.secondary]);    
  
    return (
      <Card>
        <Flex
          style={{ gap: "1rem" }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading my={0} center>
            Configuration Details
          </Heading>
        </Flex>
        <Box
          style={{
            // overflowY: "scroll",
            overflowX: "hidden",
          }}
          // maxHeight="700px"
          // maxWidth="500px"
          flexDirection="column"
          flexWrap="wrap"
          id="columnScrollable"
          textAlign="center"
          flex={1}
          m="0 auto 2rem auto"
          width={["70%", "70%", 1]}
        >
            <Table
              hideBack
              hideCreate
              columns={columns}
              totalCount={gridData.length}
              data={gridData}
              hidePager
              hideProperties
              hideSearch
              hideToggle
              style ={{border: "hidden", borderTop: "hidden"}}
              visibleHeader={false}
              visibleBorder={false}
            />
        </Box>
      </Card>
    );
  };
  
  return (
    <>
      <CreateUserModal
        show={showCreateUser}
        setShow={setShowCreateUser}
        finishConfiguration={finishConfiguration}
        sessionId={sessionID}
      />
      <CancelModal show={showCancel} setShow={setShowCancel} />

      {ui && !isLoading ? (
        <>
          <TopBar
            sessionId={sessionID}
            setShowCancel={setShowCancel}
            setShowDetails={setShowDetails}
            finishConfiguration={finishConfiguration}
            setShowCreateUser={setShowCreateUser}
            showCreateUser={showCreateUser}
          />
          <PageWrapper style={{ minHeight: "calc(100vh - 155px)" }}>
            <Flex
              pt="1rem"
              flexDirection={["column-reverse", "column-reverse", "row"]}
              style={{ gap: "1rem" }}
            >              
                <Flex flexDirection="column" flex={2}>
                  <Form
                    ImageUrl={ImageUrl}
                    isLoading={isLoading}
                    queryID={paramConfigId}
                  />
                </Flex>
                <Box
                  textAlign="center"
                  flex={1}
                  m="0 auto 2rem auto"
                  width={["70%", "70%", 1]}
                >
                  <DetailsSummary Details={Details}/>
                </Box>              
            </Flex>
          </PageWrapper>
        </>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default Configurator;
